/**
 * BottomNav Component
 * Mobile bottom navigation bar with 3 direct actions
 * Phase 2.5 - Mobile-First Responsive UI
 *
 * Visible only on screens < lg breakpoint (1200px)
 */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Psychology as SkillsIcon,
  AutoFixHigh as DirectivesIcon,
} from '@mui/icons-material';
import { PROTECTED_ROUTES } from '@/routes/routes';

interface BottomNavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
}

const bottomNavItems: BottomNavItem[] = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: PROTECTED_ROUTES.DASHBOARD },
  { label: 'Skills', icon: <SkillsIcon />, path: PROTECTED_ROUTES.SKILLS },
  { label: 'Directives', icon: <DirectivesIcon />, path: PROTECTED_ROUTES.DIRECTIVES },
];

/**
 * Determine the active nav index from the current pathname
 */
function getActiveIndex(pathname: string): number {
  const index = bottomNavItems.findIndex((item) => pathname.startsWith(item.path));
  return index >= 0 ? index : -1;
}

export function BottomNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();

  const activeIndex = useMemo(() => getActiveIndex(location.pathname), [location.pathname]);

  if (!isMobile) {
    return null;
  }

  return (
    <Paper
      component="nav"
      aria-label="Mobile navigation"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: 1,
        borderColor: 'divider',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={activeIndex}
        onChange={(_event, newValue: number) => {
          const item = bottomNavItems[newValue];
          if (item) {
            navigate(item.path);
          }
        }}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            py: 0.5,
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.625rem',
            '&.Mui-selected': {
              fontSize: '0.6875rem',
            },
          },
        }}
      >
        {bottomNavItems.map((item) => (
          <BottomNavigationAction
            key={item.label}
            label={item.label}
            icon={item.icon}
            aria-label={`Navigate to ${item.label}`}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}

/** Height of the bottom nav bar for layout padding */
export const BOTTOM_NAV_HEIGHT = 56;

export default BottomNav;
